import type { ForecastHorizons, ScenarioListResponse } from "@eu.mdotm.api/sphere";
import { Banner, Row, Text } from "@mdotm/shadcn/legacy/components";
import {
	atom,
	getDefaultStore,
	type RwAtom,
	readAtom,
	useAtomValue,
	useSetAtom,
} from "@mdotm/shadcn/legacy/reactivity";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { instanceEqualsOrFastDeepEqual, maybeCall, type SetterOrUpdaterParam } from "@mdotm/shadcn/legacy/utils";
import { Select, type SelectOption, IconButton } from "@mdotm/shadcn/mdotm/components";
import { groupBy } from "@mdotm/utils/collections";
import { useMandatoryContext } from "@mdotm/utils/react-extra";
import type { QueryKey } from "@tanstack/query-core";
import { type UseQueryOptions, useQueries } from "@tanstack/react-query";
import type { ColumnState } from "ag-grid-community";
import debounce from "debounce";
import { useEffect, useMemo, useState } from "react";
import { StackSidePanelContext } from "$root/components/common/Stack/StackSidePanel";
import { ReactQueryWrapperBase } from "$root/components/ReactQueryWrapper";
import {
	chartsComputing,
	chartsStale,
	generateColumnColor,
	reportsServeScenario,
} from "$root/functional-areas/portfolio-lab/common";
import {
	type GetPortfolioLabChartItem,
	type PortfolioLabChartItem,
	PortfolioLabContext,
} from "$root/functional-areas/portfolio-lab/ctx";
import {
	PortfolioQueryWidgetBase,
	type PortfolioWidgetData,
	WidgetStatus,
} from "$root/pages/PortfolioDetails/PortfolioWidgetStatus";
import { useMappedDynamicQueries, useMappedQuery } from "$root/third-party-integrations/react-query/hooks";
import { agExtra } from "$root/ui-lib/table/default-configuration";
import { seconds } from "$root/utils";
import { Card } from "$root/widgets-architecture/layout/Card";
import type { PerformanceSeriesData } from "$root/widgets-architecture/widgets/PortfolioPerformance/common";
import type {
	ColumnPersistence,
	ControlledSeriesVisibility,
} from "$root/widgets-architecture/widgets/PortfolioPerformance/PerformanceMetricsTable";
import {
	PerformancePanel,
	type PerformancePanelRow,
} from "$root/widgets-architecture/widgets/PortfolioPerformance/PerformancePanel";
import { useInvestmentLab } from "../../../hooks/useInvestmentLab";
import { useLabSession } from "../../../hooks/useLabSession";
import { type InternalPerformanceLab, portfolioLabRepository } from "../../../repository";
import { LabCalculatingWall } from "../LabCalculatingWall";
import { MetricsBlurOverlay } from "../MetricsBlurOverlay";
import { ShowDeltaToggle } from "../shared/ShowDeltaToggle";
import { ExAnteScenarioMetrics } from "./ExAnteScenarioMetrics";
import { PerformanceChartSettings } from "./PerformanceChartSettings";
import { labPerformanceSeriesVisibility$ } from "./state";

/** colId of the pinned, locked name column — excluded from the persisted column state. */
const NAME_COL_ID = "name";

/**
 * Sentinel id for the injected "Current Market" option. It has no server-side scenario id, but
 * `SelectOption`'s `id` is required, so it is carried through grouping under this id and remapped to
 * an empty-string value at the end (empty value === "no scenario selected" === Current Market).
 *
 * @internal — exported for tests
 */
export const CURRENT_MARKET_SENTINEL_ID = "REMOVE_ME";

/**
 * Build the grouped scenario `Select` options from the raw scenario list.
 *
 * - User-custom scenarios are dropped.
 * - A "Current Market" option is prepended; it groups alone under the "" key (so it sorts first) and
 *   its value is the empty string.
 * - The remaining scenarios split into "Forward looking scenarios" (those carrying a
 *   `forecastHorizon`) and "Historical scenarios" (those without).
 * - Groups are sorted by their key; within a group, scenarios missing an id or label are dropped.
 *
 * @internal — exported for tests
 */
export function buildScenarioOptions(data: ScenarioListResponse): [key: string, options: SelectOption<string>[]][] {
	const availableOptions = (data.scenarios ?? []).filter((s) => !s.userCustomScenario);
	const enrichedOptions = [
		{
			label: "Current Market",
			id: CURRENT_MARKET_SENTINEL_ID,
			forecastHorizon: " " as ForecastHorizons,
		},
		...availableOptions,
	];

	const groupedOptions = groupBy(enrichedOptions, (scenario) => {
		if (scenario.id === CURRENT_MARKET_SENTINEL_ID) {
			return "";
		}
		return scenario.forecastHorizon ? "Forward looking scenarios" : "Historical scenarios";
	});
	return Object.entries(groupedOptions)
		.sort(([a], [b]) => a.localeCompare(b))
		.map(([k, scenarios]): [key: string, options: SelectOption<string>[]] => [
			k,
			scenarios.flatMap((s) => {
				if (!s.id || !s.label) {
					return [];
				}
				const value = s.id === CURRENT_MARKET_SENTINEL_ID ? "" : s.id;
				return [{ label: s.label, value, "data-qualifier": `PortfolioLab/Scenario/Option(${value})` }];
			}),
		]);
}

const columnNameList = new Intl.ListFormat("en", { style: "long", type: "conjunction" });

/**
 * The warning shown while the selected scenario is one these columns' computes fell back from
 * (`FALLBACK_USED`): their numbers cover their full history instead of the scenario's window.
 */
function scenarioFallbackNotice(columnNames: string[], scenarioLabel: string): string {
	const names = columnNameList.format(columnNames);
	return columnNames.length === 1
		? `${names} has no market data for the ${scenarioLabel} window. Its line and metrics use its full history instead.`
		: `${names} have no market data for the ${scenarioLabel} window. Their lines and metrics use their full history instead.`;
}

type PerformanceChartSeries = {
	uuid: string;
	name: string;
	color: string;
	series: PerformanceSeriesData;
	/** Baseline (pre-edit) series — only feeds the metric deltas in the table, never charted. */
	baselineSeries?: PerformanceSeriesData;
};

type PerformanceChartInnerProps = {
	performanceSeries: PerformanceChartSeries[];
	chartConfig: GetPortfolioLabChartItem<"PERFORMANCE">;
	seriesVisibility: ControlledSeriesVisibility;
};

function PerformanceChartInner({ performanceSeries, chartConfig, seriesVisibility }: PerformanceChartInnerProps) {
	const ctx = useMandatoryContext(PortfolioLabContext);
	const { saveLabSession } = useLabSession();
	const rows = useMemo<PerformancePanelRow[]>(() => performanceSeries, [performanceSeries]);

	const chartUuid = chartConfig.uuid;

	// Bridge atom projecting THIS performance chart's column state over the lab-session charts atom.
	// Writes drop the locked "name" column and dedupe, so only metric columns reach the session.
	const [columns$] = useState<RwAtom<ColumnState[] | null>>(() => {
		const findChart = (charts: PortfolioLabChartItem[]) =>
			charts.find(
				(c): c is GetPortfolioLabChartItem<"PERFORMANCE"> => c.type === "PERFORMANCE" && c.uuid === chartUuid,
			);
		return atom(
			(get): ColumnState[] | null => findChart(get(ctx.charts$))?.preference.columns ?? null,
			(get, set, next: SetterOrUpdaterParam<ColumnState[] | null>) => {
				const charts = get(ctx.charts$);
				const current = findChart(charts)?.preference.columns ?? null;
				const value = maybeCall(next, current);
				const metricColumns = value?.filter((col) => col.colId !== NAME_COL_ID) ?? null;
				if (instanceEqualsOrFastDeepEqual(current, metricColumns)) {
					return;
				}
				set(
					ctx.charts$,
					charts.map((c) =>
						c.type === "PERFORMANCE" && c.uuid === chartUuid
							? {
									...c,
									preference: { ...c.preference, columns: metricColumns ?? [] },
								}
							: c,
					),
				);
			},
		);
	});

	const sync = agExtra.useSyncColStateWithAtom(columns$);
	const columnPersistence = useMemo<ColumnPersistence>(
		() => ({
			maintainColumnOrder: true,
			onStateUpdated: sync.onStateUpdated,
			onGridReady: (e) => {
				sync.onGridReady(e);
				// useSyncColStateWithAtom only applies on subsequent atom changes — apply the persisted
				// session state once on mount.
				const state = readAtom(columns$);
				if (state != null && state.length > 0) {
					e.api.applyColumnState({ state, applyOrder: true });
				}
			},
		}),
		[sync, columns$],
	);

	// Persist column changes (grid- or settings-driven) to the lab session, debounced.
	const saveLabSessionDebounced = useMemo(() => debounce(saveLabSession, seconds(1)), [saveLabSession]);

	useEffect(() => {
		const unsubscribe = getDefaultStore().sub(columns$, () => {
			saveLabSessionDebounced();
		});
		return unsubscribe;
	}, [columns$, saveLabSessionDebounced]);

	return (
		<PerformancePanel
			rows={rows}
			seriesVisibility={seriesVisibility}
			columnPersistence={columnPersistence}
			queryKeyPrefix="lab-performance"
			gridTitle="Performance chart settings"
			gridSubtitle="Manage metrics display options"
		/>
	);
}

/**
 * Historical (and Current Market) branch of the Performance chart: owns the per-investment
 * performance-series queries and renders the charted panel. Mounted ONLY while a non-forward-looking
 * scenario is selected, so its series queries exist exactly when this branch is active (the
 * forward-looking metrics live in the sibling branch — mount-scoping keeps the two query sets from
 * ever running at the same time).
 */
function HistoricalPerformance({ chartConfig }: { chartConfig: GetPortfolioLabChartItem<"PERFORMANCE"> }) {
	const ctx = useMandatoryContext(PortfolioLabContext);
	const investments = useAtomValue(ctx.investments$);
	const selectedScenarioId = useAtomValue(ctx.selectedScenarioId$);
	const metricsStatus = useAtomValue(ctx.metricsStatus$);

	// Series visibility is session-scoped and never persisted; only the charted branch uses it.
	const visibleSeries = useAtomValue(labPerformanceSeriesVisibility$);
	const setVisibleSeries = useSetAtom(labPerformanceSeriesVisibility$);
	const seriesVisibility = useMemo<ControlledSeriesVisibility>(
		() => ({ value: visibleSeries, onChange: setVisibleSeries }),
		[visibleSeries, setVisibleSeries],
	);

	const queries = useQueries({
		queries: investments.map(
			(investment): UseQueryOptions<InternalPerformanceLab, unknown, InternalPerformanceLab, QueryKey> => ({
				...portfolioLabRepository.getVirtualInvestmentPerformance.getQueryConfig(
					investment.virtualUuid,
					selectedScenarioId ?? undefined,
				),
				keepPreviousData: true,
				enabled: true,
			}),
		),
	});

	// Baseline (pre-edit) series power the in-table metric deltas. Deliberately NOT part of the
	// mapped widget query: a missing/failed baseline must never block or error the chart — the
	// metrics table just renders without the delta. Also deliberately NOT gated on `showDelta`:
	// with the repository's staleTime 0, re-enabling would refetch and can replace the on-screen
	// baseline mid-session — the toggle must stay purely presentational.
	const showDelta = chartConfig.preference.showDelta;
	const baselineQueries = useQueries({
		queries: investments.map(
			(investment): UseQueryOptions<InternalPerformanceLab, unknown, InternalPerformanceLab, QueryKey> => ({
				...portfolioLabRepository.getVirtualInvestmentPerformance.getQueryConfig(
					investment.virtualUuid,
					selectedScenarioId ?? undefined,
					true,
				),
				keepPreviousData: true,
			}),
		),
	});
	const baselineUpdatedAt = baselineQueries.map((q) => q.dataUpdatedAt);

	const performanceSeriesQuery = useMappedDynamicQueries(
		(results): PortfolioWidgetData<PerformanceChartSeries[]> => {
			if (metricsStatus === "calculating" || investments.some(chartsComputing)) {
				return { widgetStatus: WidgetStatus.CALCULATING, data: [] };
			}

			const series = investments.flatMap<PerformanceChartSeries>((investment, index) => {
				const data = results[index];
				if (!data?.series) {
					return [];
				}
				return [
					{
						uuid: investment.virtualUuid,
						name: investment.name,
						color: generateColumnColor(index),
						series: data?.series,
						// Gated on the toggle (not just the disabled query) — a previously fetched
						// baseline stays cached, and must not keep the deltas visible once hidden.
						baselineSeries: showDelta ? baselineQueries[index]?.data?.series : undefined,
					},
				];
			});
			const emptySeries = series.every((s) => s.series.length === 0);
			if (emptySeries) {
				return { widgetStatus: WidgetStatus.EMPTY, data: series };
			}

			return { widgetStatus: WidgetStatus.READY, data: series };
		},
		[investments, metricsStatus, baselineUpdatedAt, showDelta],
		queries,
	);

	return (
		<PortfolioQueryWidgetBase
			query={performanceSeriesQuery}
			iconWalls={{
				calculating: <LabCalculatingWall />,
			}}
		>
			{(series) => (
				<PerformanceChartInner
					performanceSeries={series}
					chartConfig={chartConfig}
					seriesVisibility={seriesVisibility}
				/>
			)}
		</PortfolioQueryWidgetBase>
	);
}

export function PerformanceChart({ chartConfig }: { chartConfig: GetPortfolioLabChartItem<"PERFORMANCE"> }) {
	const ctx = useMandatoryContext(PortfolioLabContext);
	const sidePanelCtx = useMandatoryContext(StackSidePanelContext);
	const investments = useAtomValue(ctx.investments$);
	const selectedScenarioId = useAtomValue(ctx.selectedScenarioId$);
	const setSelectedScenarioId = useSetAtom(ctx.selectedScenarioId$);
	const { reloadInvestmentsMetrics } = useInvestmentLab();

	const scenariosQuery = portfolioLabRepository.getHistoricalScenario.useQuery();

	// Built once per data change; reused for the O(1) selected-scenario lookup below.
	const scenarioById = useMemo(
		() => new Map((scenariosQuery.data?.scenarios ?? []).map((s) => [s.id, s] as const)),
		[scenariosQuery.data],
	);

	const scenarioOptionsQuery = useMappedQuery(buildScenarioOptions, [], scenariosQuery);

	// Forward-looking scenarios carry a `forecastHorizon`; historical ones (and Current Market) do not.
	const isForwardLookingScenarioId = (id: string | null) => id != null && scenarioById.get(id)?.forecastHorizon != null;
	const isForwardLookingScenario = isForwardLookingScenarioId(selectedScenarioId);

	const title = "Portfolio performance";

	// Same guard as the exposure category selects: while a column's charts are stale, switching
	// scenario refetches against the server's last computed composition and presents it as the new
	// scenario's numbers. Lock the scenario until the recompute lands. This also withholds the
	// forward-looking targets, which would compute — one rule the user can predict beats a per-option
	// gate the Select cannot express. A queued compute locks it too: it would store its scenario back
	// over the newer choice when it finishes.
	const lockScenario = investments.some((investment) => chartsStale(investment) || chartsComputing(investment));

	const fallbackColumns = investments.filter(
		(investment) => selectedScenarioId != null && investment.scenarioFallback?.scenarioId === selectedScenarioId,
	);
	const selectedScenarioLabel =
		selectedScenarioId != null ? (scenarioById.get(selectedScenarioId)?.label ?? "selected scenario") : null;

	return (
		<MetricsBlurOverlay qualifier="PortfolioLab/Chart(PERFORMANCE)" enableBlur={lockScenario}>
			<Card
				className="min-h-[200px]"
				title={
					<Row alignItems="center" gap={8}>
						<Text type="Body/XL/Bold" title={title} className="shrink-0">
							{title}
						</Text>
						<ReactQueryWrapperBase query={scenarioOptionsQuery} loadingFallback={null}>
							{(scenarioOptions) => (
								<Select
									value={selectedScenarioId ?? ""}
									disabled={lockScenario}
									onChange={(id) => {
										const nextId = id || null;
										setSelectedScenarioId(nextId);
										// Forward-looking scenarios always compute; historical ones and Current Market reuse a
										// column's stored reports when those serve them, and compute it otherwise.
										const forwardLooking = isForwardLookingScenarioId(nextId);
										reloadInvestmentsMetrics(investments, {
											skipCompute: (investment) => !forwardLooking && reportsServeScenario(investment, nextId),
										});
									}}
									options={scenarioOptions}
									size="x-small"
									triggerDataAttrs={{ "data-qualifier": "PortfolioLab/Scenario/Select" }}
								/>
							)}
						</ReactQueryWrapperBase>
					</Row>
				}
				actionHeader={
					<Row alignItems="center" gap={16}>
						<ShowDeltaToggle chart={chartConfig} />
						{!isForwardLookingScenario && (
							<IconButton
								icon="Settings"
								size={20}
								color={themeCSSVars.palette_N500}
								onClick={() => sidePanelCtx.push(PerformanceChartSettings)}
							/>
						)}
					</Row>
				}
			>
				{fallbackColumns.length > 0 && selectedScenarioLabel != null && (
					<Banner severity="warning" className="mb-2" data-qualifier="PortfolioLab/Performance/FallbackBanner">
						{scenarioFallbackNotice(
							fallbackColumns.map((investment) => investment.name),
							selectedScenarioLabel,
						)}
					</Banner>
				)}
				{isForwardLookingScenario && selectedScenarioId != null ? (
					<ExAnteScenarioMetrics scenarioId={selectedScenarioId} showDelta={chartConfig.preference.showDelta} />
				) : (
					<HistoricalPerformance chartConfig={chartConfig} />
				)}
			</Card>
		</MetricsBlurOverlay>
	);
}
